"use client";

import {
  createContext,
  useContext,
  useEffect,
  useState,
  type ReactNode,
} from "react";
import { flushSync } from "react-dom";

// Тема операторской консоли: светлая / тёмная / авто (по системе). Дефолт — светлая.
// Выбор помнится в localStorage; резолвленная тема кладётся в data-theme на .op-root,
// где globals.css переключает семантические токены (--op-*).

export type OpTheme = "light" | "dark" | "auto";
const KEY = "mise_op_theme";

const ThemeCtx = createContext<{
  theme: OpTheme;
  /** Во что `theme` разрешилась сейчас. Для `auto` — то, что стоит в системе. */
  resolved: "light" | "dark";
  setTheme: (t: OpTheme) => void;
}>({
  theme: "light",
  resolved: "light",
  setTheme: () => {},
});
export const useOpTheme = () => useContext(ThemeCtx);

function systemDark(): boolean {
  return typeof window !== "undefined" && window.matchMedia("(prefers-color-scheme: dark)").matches;
}
function resolve(t: OpTheme): "light" | "dark" {
  return t === "auto" ? (systemDark() ? "dark" : "light") : t;
}

export function ThemeProvider({
  fontClass,
  surface,
  children,
}: {
  fontClass: string;
  /**
   * Какая это поверхность. Тема одна на консоль и на рабочее место, но акцент
   * у них РАЗНЫЙ (решение Марата 06.08): у клиента оранжевый KLAIT, у консоли
   * оператора синий. Причина не декоративная — это два разных продукта в одной
   * вкладке браузера, и по цвету должно быть с первого взгляда видно, где ты
   * сейчас: в своём заведении или в служебной консоли над всеми клиентами.
   *
   * Переопределение висит на `data-surface` (см. globals.css): меняются только
   * акцентные токены, вся остальная тема общая.
   */
  surface?: "operator";
  children: ReactNode;
}) {
  // ⚠️ Тёмная тема оставлена ТОЛЬКО в консоли оператора (решение 07.09.2026:
  // «пусть будет всё в светлой теме, из operator можешь не удалять»). Этот же
  // провайдер оборачивает /desk и /desk2 — без этой развилки рабочее место
  // уезжало в тёмную само, по системной настройке ноутбука.
  const themed = surface === "operator";

  const [theme, setThemeState] = useState<OpTheme>("light");
  const [resolved, setResolved] = useState<"light" | "dark">("light");

  // Восстановление выбора (после маунта, чтобы не ломать SSR-разметку).
  useEffect(() => {
    if (!themed) return;
    const saved = localStorage.getItem(KEY) as OpTheme | null;
    if (saved === "light" || saved === "dark" || saved === "auto") setThemeState(saved);
  }, [themed]);

  // Резолв + подписка на смену системной темы (только в режиме «авто»).
  useEffect(() => {
    if (!themed) {
      setResolved("light");
      return;
    }
    setResolved(resolve(theme));
    if (theme !== "auto") return;
    const mq = window.matchMedia("(prefers-color-scheme: dark)");
    const on = () => setResolved(resolve("auto"));
    mq.addEventListener("change", on);
    return () => mq.removeEventListener("change", on);
  }, [theme, themed]);

  // Гасим ВСЕ переходы на момент смены темы (маунт light→resolved и тумблер): иначе
  // элементы с transition:background-color «залипают» на старом var-значении — Chrome
  // не интерполирует смену var() в переходе. Instant-flip темы + переходы для hover целы.
  useEffect(() => {
    const css = document.createElement("style");
    // ⚠️ Шайба переключателя темы ИСКЛЮЧЕНА из глушителя. Иначе выходил
    // замкнутый круг: жмёшь «Тёмная» → меняется `resolved` → на кадр гасятся
    // ВСЕ переходы → и в этот самый кадр шайба должна была ехать. Она не
    // ехала, а телепортировалась. Марат так и сказал: «ползунок без красивой
    // анимации». Всё остальное гасится по-прежнему.
    css.textContent =
      ".op-root, .op-root *:not([data-theme-knob]) { transition: none !important; }";
    document.head.appendChild(css);
    const id = requestAnimationFrame(() => {
      void document.body.offsetHeight; // форс-reflow, чтобы новые цвета применились без перехода
      requestAnimationFrame(() => css.remove());
    });
    // ⚠️ Страховка таймером. Уборка висела ТОЛЬКО на requestAnimationFrame, а он
    // в скрытой вкладке не идёт вовсе — и глушитель оставался в <head> до
    // возвращения на вкладку. Пока он там, в приложении не работает ни один
    // переход и ни одна анимация: ни переезд пилюль, ни мерцание огня.
    // Само по себе это лечилось возвратом (rAF просыпается), но держать
    // глобальный «выключатель движения» на том, отрисовывается ли страница, —
    // плохая опора. Таймер идёт и в фоне.
    const fallback = setTimeout(() => css.remove(), 120);
    return () => {
      cancelAnimationFrame(id);
      clearTimeout(fallback);
      css.remove();
    };
  }, [resolved]);

  /**
   * Смена темы — ПЛАВНЫМ переходом всего экрана (правка Марата 08.08:
   * «очень бьёт по глазам»).
   *
   * ⚠️ Почему нельзя просто добавить `transition` цветам. Тема переключается
   * подменой CSS-переменных на корне, а браузер значения `var()` в переходе НЕ
   * интерполирует: цвет прыгает в конце перехода, а элемент всю дорогу стоит
   * на старом. Ровно из-за этого здесь и появился глушитель переходов —
   * мгновенный переворот был меньшим злом, чем залипшие цвета.
   *
   * View Transitions решают это иначе и правильно: браузер снимает кадр СТАРОГО
   * экрана, применяет изменение и плавно растворяет старый кадр в новом. Ему
   * всё равно, что поменялось — хоть все переменные разом.
   *
   * `flushSync` обязателен: снимок «после» делается сразу по возврату из
   * колбэка, а обычный setState к тому моменту ещё не применён — и растворять
   * было бы нечего.
   *
   * Где API нет (старые Safari и Firefox) — прежнее поведение: мгновенно, без
   * ошибок. Тема важнее красоты перехода, поэтому это честный запасной путь.
   */
  const setTheme = (t: OpTheme) => {
    // Вне консоли темы нет — и переключать нечего (см. `themed` выше).
    if (!themed) return;
    const apply = () => {
      setThemeState(t);
      try {
        localStorage.setItem(KEY, t);
      } catch {
        /* приватный режим — просто не помним */
      }
    };

    const doc = document as Document & {
      startViewTransition?: (cb: () => void) => {
        ready: Promise<void>;
        finished: Promise<void>;
      };
    };
    if (typeof doc.startViewTransition !== "function") {
      apply();
      return;
    }
    const vt = doc.startViewTransition(() => flushSync(apply));
    // ⚠️ Промисы перехода ОБЯЗАТЕЛЬНО гасить. Браузер отменяет переход, когда
    // страница не отрисовывается (вкладка в фоне) или когда сразу запускают
    // второй — и `ready` отклоняется с InvalidStateError. Непойманное
    // отклонение всплывает в консоль, а в dev Next показывает его плашкой
    // «1 Issue»: выглядит как поломка, хотя тема переключилась нормально.
    // Отмена перехода — это «показали без анимации», а не ошибка.
    vt.ready.catch(() => {});
    vt.finished.catch(() => {});
  };

  return (
    <ThemeCtx.Provider value={{ theme, resolved, setTheme }}>
      <div
        data-theme={resolved}
        data-surface={surface}
        className={`op-root op-scene ${fontClass} min-h-dvh text-[var(--op-fg)] antialiased [font-feature-settings:'ss01','cv11'] [color-scheme:light] data-[theme=dark]:[color-scheme:dark] selection:bg-[var(--op-lime)] selection:text-[var(--op-lime-ink)]`}
      >
        {/* Моушн-токены консоли (один источник кривой/длительностей входа). */}
        <style>{`
          @keyframes op-rise{from{opacity:0;transform:translateY(10px)}to{opacity:1;transform:none}}
          .op-rise{animation:op-rise .5s var(--ease-out) both}
          @keyframes op-grow-y{from{transform:scaleY(0)}to{transform:scaleY(1)}}
          @keyframes op-grow-x{from{transform:scaleX(0)}to{transform:scaleX(1)}}
          @keyframes op-pop{from{opacity:0;transform:translateY(4px) scale(.9)}to{opacity:1;transform:none}}
          @media (prefers-reduced-motion: reduce){@keyframes op-rise{from{opacity:0;transform:none}to{opacity:1;transform:none}}@keyframes op-grow-y{from{transform:none}to{transform:none}}@keyframes op-grow-x{from{transform:none}to{transform:none}}@keyframes op-pop{from{opacity:0;transform:none}to{opacity:1;transform:none}}}
          /* «Режим оптимизации» (N2-Б) — копия блока выше под html[data-motion="reduced"]
             (см. src/lib/mobile/motion.ts); @keyframes внутри обычного селектора
             синтаксисом CSS не допускаются (в отличие от @media), поэтому здесь
             отдельные имена и переключение через html[data-motion="reduced"] .op-rise. */
          @keyframes op-rise-reduced{from{opacity:0;transform:none}to{opacity:1;transform:none}}
          html[data-motion="reduced"] .op-rise{animation-name:op-rise-reduced}
        `}</style>
        {children}
      </div>
    </ThemeCtx.Provider>
  );
}
